import React, { useEffect, useRef, useState } from 'react';
import { collection, getDocs, query, orderBy, limit, where, getCountFromServer } from 'firebase/firestore';
import { db } from '../services/firebase';
import { fetchClientesDoUsuario } from '../services/clientsQuery';
import { CheckIn, User, PAPEIS_FILTRAVEIS } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useAccessScope } from '../hooks/useAccessScope';

// Mora em ../types agora (o `managedSellers` também precisa dela). Re-exportado aqui para não
// quebrar quem já importava deste arquivo (CheckInsMapPage).
export { PAPEIS_FILTRAVEIS };

// Conta os clientes ATIVOS de UM vendedor (por sellerId=uid E por sankhya.codvend, número e string),
// via queries de campo único (sem índice composto), deduplicando por id. O `fetchClientesDoUsuario`
// não serve aqui porque, para admin, o `isFullAccess` ignora o escopo e devolve a base inteira.
async function contarClientesAtivosDoVendedor(sellerId: string, codvend?: number | null): Promise<number> {
  const col = collection(db, 'clients');
  const qs = [getDocs(query(col, where('sellerId', '==', sellerId)))];
  if (codvend != null) {
    qs.push(getDocs(query(col, where('sankhya.codvend', '==', codvend))));
    qs.push(getDocs(query(col, where('sankhya.codvend', '==', String(codvend)))));
  }
  const byId = new Map<string, any>();
  (await Promise.all(qs)).forEach((sn) => sn.forEach((d) => byId.set(d.id, d.data())));
  return Array.from(byId.values()).filter((c) => c.active !== false).length;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const scope = useAccessScope();
  const [stats, setStats] = useState({ checkins: 0, clients: 0, sellers: 0, today: 0 });
  const [recent, setRecent] = useState<CheckIn[]>([]);
  const [loading, setLoading] = useState(true);
  const [sellers, setSellers] = useState<User[]>([]);   // vendedores-usuários (quem faz check-in) — p/ o filtro
  const [filtroSellerId, setFiltroSellerId] = useState(''); // '' = todos
  const [sellerInput, setSellerInput] = useState('');   // texto digitado no filtro (código OU nome)
  const [sellerFocus, setSellerFocus] = useState(false); // dropdown aberto (foco no campo)
  const loadSeq = useRef(0);

  // Carrega os usuários do filtro: TODOS os perfis que trabalham em campo — vendedor, supervisor,
  // coordenador, gerente, gerente nacional, pricing e diretor (não só 'seller': gestores também fazem
  // check-in e precisam ser filtráveis). Admin vê todos; gestor escopado só a equipe dele.
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        let arr = (snap.docs.map((d) => ({ id: d.id, ...d.data() })) as User[])
          .filter((u) => PAPEIS_FILTRAVEIS.has(u.role) && (u as any).active !== false);
        if (!scope.full) { const idSet = new Set(scope.sellerIds || []); arr = arr.filter((s) => idSet.has(s.id)); }
        arr.sort((a, b) => (a.codVendSankhya ?? 1e9) - (b.codVendSankhya ?? 1e9));
        setSellers(arr);
      } catch { setSellers([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  useEffect(() => {
    const seq = ++loadSeq.current;                 // guarda de sequência: só a carga MAIS NOVA aplica o estado
    const stale = () => seq !== loadSeq.current;   // (o escopo do gestor carrega async → evita race sobrescrever)
    const load = async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const mapRecent = (docs: any[]) => docs.map((d) => { const raw = d.data(); return { id: d.id, ...raw, createdAt: raw.createdAt?.toDate() ?? new Date() }; }) as CheckIn[];

      try {
        // FILTRO POR VENDEDOR: números só do sellerId escolhido (check-ins por sellerId; clientes por
        // sellerId/codvend). Vale para admin e gestor (o gestor só enxerga vendedores da equipe no filtro).
        if (filtroSellerId) {
          const fs = sellers.find((s) => s.id === filtroSellerId);
          const [totalAgg, todayAgg, recentSnap, clientsCount] = await Promise.all([
            getCountFromServer(query(collection(db, 'checkins'), where('sellerId', '==', filtroSellerId))),
            getCountFromServer(query(collection(db, 'checkins'), where('sellerId', '==', filtroSellerId), where('createdAt', '>=', today))),
            getDocs(query(collection(db, 'checkins'), where('sellerId', '==', filtroSellerId), orderBy('createdAt', 'desc'), limit(5))),
            contarClientesAtivosDoVendedor(filtroSellerId, fs?.codVendSankhya),
          ]);
          if (stale()) return;
          setStats({ checkins: totalAgg.data().count, clients: clientsCount, sellers: 1, today: todayAgg.data().count });
          setRecent(mapRecent(recentSnap.docs));
          return;
        }

        if (scope.full) {
          // Acesso total: contagens agregadas no servidor (não baixa os documentos).
          const [totalAgg, todayAgg, clientsAgg, sellersAgg, recentSnap] = await Promise.all([
            getCountFromServer(collection(db, 'checkins')),
            getCountFromServer(query(collection(db, 'checkins'), where('createdAt', '>=', today))),
            getCountFromServer(query(collection(db, 'clients'), where('active', '==', true))),
            getCountFromServer(query(collection(db, 'users'), where('role', '==', 'seller'))),
            getDocs(query(collection(db, 'checkins'), orderBy('createdAt', 'desc'), limit(5))),
          ]);
          if (stale()) return;
          setStats({ checkins: totalAgg.data().count, clients: clientsAgg.data().count, sellers: sellersAgg.data().count, today: todayAgg.data().count });
          setRecent(mapRecent(recentSnap.docs));
          return;
        }

        // Gestor: contagens restritas à hierarquia dele — sellerIds em LOTES de 30 no 'in'
        // (antes o slice(0,30) IGNORAVA silenciosamente vendedores além do 30º → KPIs subcontados).
        const ids = (scope.sellerIds || []).filter(Boolean);
        if (ids.length === 0 && (scope.codvends || []).length === 0) {
          if (stale()) return;
          setStats({ checkins: 0, clients: 0, sellers: 0, today: 0 }); setRecent([]); return;
        }
        const chunk = <T,>(a: T[], n: number) => { const o: T[][] = []; for (let i = 0; i < a.length; i += n) o.push(a.slice(i, i + n)); return o; };
        const idChunks = chunk(ids, 30);
        const [clientesEscopo, totalAggs, todayAggs, recentChunks] = await Promise.all([
          fetchClientesDoUsuario(user, scope).catch(() => [] as any[]), // falha na leitura de clientes degrada SÓ o KPI de clientes (não zera os demais) — espelha o catch interno do loadClientsCached do app

          Promise.all(idChunks.map((c) => getCountFromServer(query(collection(db, 'checkins'), where('sellerId', 'in', c))))),
          Promise.all(idChunks.map((c) => getCountFromServer(query(collection(db, 'checkins'), where('sellerId', 'in', c), where('createdAt', '>=', today))))),
          // recentes da EQUIPE por lote (não do global-50) — senão a lista some quando outros times enchem o topo.
          Promise.all(idChunks.map((c) => getDocs(query(collection(db, 'checkins'), where('sellerId', 'in', c), orderBy('createdAt', 'desc'), limit(5))))),
        ]);
        if (stale()) return;
        const checkinsTotal = totalAggs.reduce((s, a) => s + a.data().count, 0);
        const todayTotal = todayAggs.reduce((s, a) => s + a.data().count, 0);
        const clientsCount = (clientesEscopo as any[]).filter((c) => c.active === true).length; // mesmo critério do acesso total
        const recentDocs = recentChunks
          .flatMap((sn) => sn.docs)
          .sort((a, b) => ((b.data() as any).createdAt?.toMillis?.() ?? 0) - ((a.data() as any).createdAt?.toMillis?.() ?? 0))
          .slice(0, 5);
        setStats({ checkins: checkinsTotal, clients: clientsCount, sellers: (scope.sellerIds || []).filter((id) => id !== user?.id).length, today: todayTotal });
        setRecent(mapRecent(recentDocs));
      } catch (e) {
        console.warn('Dashboard: erro ao carregar', e); // não deixa o "Carregando..." travado se uma leitura falhar
      } finally {
        if (!stale()) setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, filtroSellerId]);

  const formatDate = (d: Date) => d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  const kpis = [
    { label: 'Check-ins Hoje', value: stats.today, icon: '📍', color: '#f5821f' },
    { label: 'Total de Check-ins', value: stats.checkins, icon: '📋', color: '#7c3aed' },
    { label: 'Clientes Ativos', value: stats.clients, icon: '👥', color: '#059669' },
    { label: 'Vendedores', value: stats.sellers, icon: '🧑‍💼', color: '#d97706' },
  ];

  const vendedorSel = sellers.find((s) => s.id === filtroSellerId);
  const vendLabel = (s: User) => `${s.codVendSankhya ? `${s.codVendSankhya} - ` : ''}${s.name}`;
  const committedLabel = vendedorSel ? vendLabel(vendedorSel) : '';
  // Typeahead LOCAL (código OU nome) sobre os vendedores já carregados — rápido e escala p/ dezenas.
  const buscaVend = sellerInput.trim().toLowerCase();
  const sellerOpcoes = (!buscaVend || sellerInput === committedLabel) ? [] : sellers.filter((s) =>
    String(s.codVendSankhya ?? '').includes(buscaVend) || (s.name || '').toLowerCase().includes(buscaVend)
  ).slice(0, 30);
  const selecionarVendedor = (s: User) => { setFiltroSellerId(s.id); setSellerInput(vendLabel(s)); setSellerFocus(false); };
  const limparFiltro = () => { setFiltroSellerId(''); setSellerInput(''); };

  return (
    <div>
      <h1 style={styles.greeting}>Olá, {user?.name?.split(' ')[0]} 👋</h1>
      <p style={styles.subtitle}>
        {vendedorSel
          ? `Resumo de ${vendedorSel.codVendSankhya ? `${vendedorSel.codVendSankhya} - ` : ''}${vendedorSel.name}`
          : 'Aqui está um resumo da equipe'}
      </p>

      {sellers.length > 0 && (
        <div style={styles.filterRow}>
          <label style={styles.filterLabel}>Vendedor:</label>
          <div style={{ position: 'relative', minWidth: 300 }}>
            <input
              style={styles.filterInput}
              placeholder="Buscar por código ou nome…"
              value={sellerInput}
              onChange={(e) => setSellerInput(e.target.value)}
              onFocus={() => setSellerFocus(true)}
              onBlur={() => setSellerFocus(false)}
              autoComplete="off"
            />
            {(sellerInput || filtroSellerId) && (
              <button type="button" style={styles.filterClear} title="Limpar" onMouseDown={(e) => { e.preventDefault(); limparFiltro(); }}>✕</button>
            )}
            {sellerFocus && sellerOpcoes.length > 0 && (
              <div style={styles.filterDropdown}>
                {sellerOpcoes.map((s) => (
                  <div key={s.id} style={styles.filterOption} onMouseDown={(e) => { e.preventDefault(); selecionarVendedor(s); }}>
                    {vendLabel(s)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {loading ? <p>Carregando...</p> : (
        <>
          <div style={styles.kpiGrid}>
            {kpis.map(k => (
              <div key={k.label} style={styles.kpiCard}>
                <div style={{ ...styles.kpiIcon, background: k.color + '15', color: k.color }}>{k.icon}</div>
                <div style={{ ...styles.kpiValue, color: k.color }}>{k.value}</div>
                <div style={styles.kpiLabel}>{k.label}</div>
              </div>
            ))}
          </div>

          <h2 style={styles.sectionTitle}>Últimos Check-ins</h2>
          <div style={styles.recentList}>
            {recent.map(ci => (
              <div key={ci.id} style={styles.recentItem}>
                <div style={styles.recentIcon}>📍</div>
                <div style={styles.recentContent}>
                  <div style={styles.recentTitle}>{ci.clientName}</div>
                  <div style={styles.recentSub}>por {ci.sellerName}</div>
                </div>
                <div style={styles.recentDate}>{formatDate(ci.createdAt)}</div>
              </div>
            ))}
            {recent.length === 0 && <p style={{ color: '#999' }}>Nenhum check-in registrado ainda.</p>}
          </div>
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  greeting: { fontSize: 26, fontWeight: 700, color: '#1a1a2e', margin: '0 0 4px' },
  subtitle: { color: '#666', margin: '0 0 20px', fontSize: 15 },
  filterRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28, flexWrap: 'wrap' },
  filterLabel: { fontSize: 14, fontWeight: 600, color: '#555' },
  filterInput: { width: '100%', boxSizing: 'border-box', border: '1px solid #e0e0e0', borderRadius: 10, padding: '9px 34px 9px 12px', fontSize: 14, background: '#fff' },
  filterClear: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#999', fontSize: 15, cursor: 'pointer', padding: 4, lineHeight: 1 },
  filterDropdown: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', maxHeight: 280, overflowY: 'auto' },
  filterOption: { padding: '10px 12px', fontSize: 14, color: '#333', cursor: 'pointer', borderBottom: '1px solid #f5f5f5' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 40 },
  kpiCard: { background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 8px rgba(0,0,0,0.06)', textAlign: 'center' },
  kpiIcon: { width: 48, height: 48, borderRadius: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, margin: '0 auto 12px' },
  kpiValue: { fontSize: 36, fontWeight: 800 },
  kpiLabel: { fontSize: 13, color: '#666', marginTop: 4 },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 16 },
  recentList: { background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  recentItem: { display: 'flex', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #f5f5f5' },
  recentIcon: { fontSize: 20, marginRight: 14 },
  recentContent: { flex: 1 },
  recentTitle: { fontWeight: 600, color: '#1a1a2e', fontSize: 15 },
  recentSub: { fontSize: 13, color: '#666', marginTop: 2 },
  recentDate: { fontSize: 12, color: '#999' },
};
